import { useAuth0 } from "@auth0/auth0-react";
import Switch from "@mui/material/Switch";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import {
	ComponentPropsContext,
	ComponentPropsContextApi
} from "layouts/Flow Designer/context";
import CredentialDeleteDialog from "layouts/pages/credential-management/components/CredentialDeleteDialog";
import CredentialDialogForm from "layouts/pages/credential-management/components/CredentialDialogForm";
import CredentialManangementAction from "layouts/pages/credential-management/components/CredentialManagementAction";
import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import SelectInput from "../SelectInput";

import { getCredentialById } from "api/CredentialManagementApi";
import { useCredentials } from "../../../../../../hooks/useCredentials";
import { useResourceListener } from "hooks/useResourceListener";
import { REQUIRED_CREDENTIAL_COMPONENTS } from "constants/flowDesigner";

export const CREDENTIAL_REQUIRED = "credentialRequired";
export const CREDENTIAL_TYPE = "credentialType";
export const ORIGINAL_CREDENTIAL_TYPE = "originalCredentialType";

const CredentialListItem = ({ action, fieldValue, allowEditWorkflow }) => {
	const { user } = useAuth0();
	const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
	const { actionForm, componentAction, formErrors } = useContext(
		ComponentPropsContext
	);
	const { setActionForm, setFormErrors } = useContext(
		ComponentPropsContextApi
	);
	const [credentialOptions, setCredentialOptions] = useState([]);
	const [credentialData, setCredentialData] = useState({});
	const isCredentialSupported =
		componentAction?.isCredentialSupported === true;
	const isCredentialRequired = REQUIRED_CREDENTIAL_COMPONENTS.has(componentAction?.componentType);
	const isCommonComponent =
		typeof componentAction?.actionID === "string" &&
		componentAction.actionID.startsWith("CC_");
	const isMcpBearerAuthentication =
		componentAction?.actionID === "CC_mcp_configure" &&
		(actionForm.authenticationType?.value === "bearerToken" ||
			(!actionForm.authenticationType?.value && actionForm[CREDENTIAL_REQUIRED]?.value));
	const isMcpClient = componentAction?.actionID === "CC_mcp_configure";
	const { credentials, handleFetchCredentials } = useCredentials();

	useResourceListener("credentialsSharingUpdate", handleFetchCredentials);

	const handleSelectCredentialChange = (e) => {
		const { value } = e;

		const foundCredential = credentials.find(
			(credential) => credential.id === value
		);

		setActionForm({
			...actionForm,
			[action.field]: {
				...actionForm[action.field],
				value
			},
			credentialType: {
				value: foundCredential?.application?.applicationType ?? "database"
			}
		});
	};

	const handleCloseCredentialDialog = (credential) => {
		if (credential && credential.length) {
			const newCredential = credential[credential.length - 1];
			setActionForm({
				...actionForm,
				[action.field]: {
					...actionForm[action.field],
					value: newCredential.id
				}
			});
		}
	};

	const handleCredentialRequired = () => {
		const credentialRequiredFields = actionForm[CREDENTIAL_REQUIRED];

		if (!credentialRequiredFields) {
			action.isRequired = true;

			return setActionForm({
				...actionForm,
				[CREDENTIAL_REQUIRED]: {
					value: true
				}
			});
		}

		action.isRequired = credentialRequiredFields.value ? false : true;

		setActionForm({
			...actionForm,
			[CREDENTIAL_REQUIRED]: {
				value: credentialRequiredFields.value ? false : true
			}
		});

		if (credentialRequiredFields.value && formErrors.includes("credential")) {
			setFormErrors((errors) =>
				errors.filter((error) => error !== "credential")
			);
		}
	};

	const fetchSharedFlowCredential = async (options) => {
		const credential = await getCredentialById(
			currentSavedFlow.tenant_id,
			fieldValue
		);

		if (credential?.isSuccessful && credential?.resultObject) {
			setCredentialOptions([
				...options,
				{
					label: credential?.resultObject?.application?.name,
					value: credential?.resultObject?.id
				}
			]);
		} else {
			setCredentialOptions(options);
		}
	};

	useEffect(() => {
		if (fieldValue && credentials) {
			const selectedCredentialData = credentials.find(
				(cred) => cred.id === fieldValue
			);
			setCredentialData(selectedCredentialData);
		} else {
			setCredentialData(null);
		}
	}, [fieldValue, credentials]);

	useEffect(() => {
		if (!user?.stripeId || !user?.cxfUserId) return;

		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				handleFetchCredentials();
			}
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			document.removeEventListener(
				"visibilitychange",
				handleVisibilityChange
			);
		};
	}, [user?.stripeId, user?.cxfUserId]);

	useEffect(() => {
		if (!credentials.length) {
			setCredentialOptions([]);
			return;
		}

		const applicationType = componentAction?.applicationType || [];
		const options =
			credentials
				?.filter((credential) =>
					applicationType.includes(
						credential?.application?.applicationType
					)
				)
				?.map((credential) => ({
					label: credential?.application?.name,
					value: credential?.id
				})) || [];

		if (
			fieldValue &&
			!options?.some((option) => option.value === fieldValue)
		) {
			fetchSharedFlowCredential(options);
		} else {
			setCredentialOptions(options);
		}
	}, [credentials, componentAction, fieldValue]);

	if (isMcpClient && !isMcpBearerAuthentication) {
		return null;
	}

	return (
		<>
			<SoftBox
				display="flex"
				alignItems="start"
				gap={2}
				width="100%"
			>
				<SoftBox
					position="relative"
					width="100%"
				>
					{isCredentialSupported &&
						(!isCommonComponent ||
							isCredentialRequired ||
							actionForm[CREDENTIAL_REQUIRED]?.value) && (
							<SelectInput
								menuPlacement="bottom"
								menuPosition="fixed"
								options={credentialOptions}
								action={action}
								fieldValue={fieldValue}
								handleChange={handleSelectCredentialChange}
								disabled={!allowEditWorkflow}
							/>
						)}

					{formErrors.includes("credential") && (
						<SoftTypography
							sx={{
								color: "#ff667c",
								fontSize: "13px"
							}}
						>
							Credential is required
						</SoftTypography>
					)}

					{actionForm.credential?.value &&
						actionForm[CREDENTIAL_TYPE]?.value &&
						actionForm[CREDENTIAL_REQUIRED]?.value && (
							<SoftTypography
								color="text"
								sx={{
									fontSize: "13px",
									marginTop: "4px",
									opacity: ".7"
								}}
							>
								Credential Type:{" "}
								<span style={{ textTransform: "uppercase" }}>
									{actionForm[CREDENTIAL_TYPE].value}
								</span>
							</SoftTypography>
						)}

					{actionForm[ORIGINAL_CREDENTIAL_TYPE]?.value && (
						<SoftTypography
							color="warning"
							sx={{
								fontSize: "13px",
								marginTop: "4px",
								opacity: ".7"
							}}
						>
							ⓘ The credential is required. Please make sure to create or
							use{" "}
							<span style={{ textTransform: "uppercase" }}>
								{actionForm[ORIGINAL_CREDENTIAL_TYPE].value}
							</span>{" "}
							credential
						</SoftTypography>
					)}
				</SoftBox>

				<SoftBox>
					{isCommonComponent && !isMcpClient && !isCredentialRequired && (
						<>
							<SoftBox
								className="credential-switch"
								sx={{
									alignItems: "center",
									display: "flex",
									inset: "0 0 auto auto",
									position: actionForm[CREDENTIAL_REQUIRED]?.value
										? "absolute"
										: "relative",
									width: "max-content"
								}}
							>
								<Switch
									sx={{
										marginRight: "4px"
									}}
									checked={
										actionForm[CREDENTIAL_REQUIRED]?.value === true
									}
									onChange={handleCredentialRequired}
									disabled={action?.isDisabled || !allowEditWorkflow}
								/>
								<SoftTypography
									color="text"
									sx={{
										fontSize: "13px"
									}}
								>
									Requires Credential
								</SoftTypography>
							</SoftBox>
						</>
					)}
					{(isCredentialSupported && !isCommonComponent) ||
					(isCredentialSupported &&
						isCommonComponent &&
						actionForm[CREDENTIAL_REQUIRED]?.value) ? (
						<SoftBox marginTop="32px">
							<CredentialManangementAction
								credentialData={credentialData}
								iconOrientation="vertical"
								isEditor
								allowEditWorkflow={allowEditWorkflow}
							/>
						</SoftBox>
					) : null}
				</SoftBox>
			</SoftBox>

			<CredentialDialogForm
				optionApplicationTypes={componentAction?.applicationType}
				onClose={handleCloseCredentialDialog}
			/>
			<CredentialDeleteDialog />
		</>
	);
};

export default CredentialListItem;
